import {
  DefaultError,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryClientProvider,
  UndefinedInitialDataOptions,
  UseQueryResult,
  useQuery
} from "@tanstack/react-query";
import { ReactNode } from "react";

interface QueryKeyBase {
  component: string;
}

interface BackendQueryKey extends QueryKeyBase {
  baseUrl: string;
  endpoint: string;
  params?: Record<string, unknown>;
}

interface LocalQueryKey extends QueryKeyBase {
  data: Record<string, unknown> | string;
}

enum ORIGIN {
  Local = 1,
  Backend
}

interface TypedQueryKeyBase<T> {
  origin: ORIGIN;
  dataType?: T; // dummy just to reference the type that we want to store for that key, never actually gets a value.
}

type TypedLocalQueryKey<T> = TypedQueryKeyBase<T> & LocalQueryKey;
type TypedBackendQueryKey<T> = TypedQueryKeyBase<T> & BackendQueryKey;

type MissingQueryKeyType = "Missing Query Key Type";
type MandatoryQueryKeyType<T> = [T] extends [never] ? MissingQueryKeyType : unknown extends T ? MissingQueryKeyType : undefined extends T ? MissingQueryKeyType : T;
type NotAny<T> = 0 extends 1 & T ? never : T;

export type TypedQueryKey<T> = [T] extends MissingQueryKeyType ? TypedQueryKey<"Typed Query Key"> : TypedBackendQueryKey<T> | TypedLocalQueryKey<T>;

export function localQueryKey<T>(queryKey: LocalQueryKey): TypedLocalQueryKey<MandatoryQueryKeyType<T>> {
  return { origin: ORIGIN.Local, ...queryKey };
}

export function backendQueryKey<T>(queryKey: BackendQueryKey): TypedBackendQueryKey<MandatoryQueryKeyType<T>> {
  return { origin: ORIGIN.Backend, ...queryKey };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000 * 60 * 24, // Our sessions are 24h long
      refetchOnReconnect: false //,
      // TODO: when all local keys have been moved to typed keys uncomment the following line ( until then we cannot because it clear up cache if a local key is not currently rendered )
      //gcTime: 0 // Once a key is not used by any mounted component we clean it up directly by default, local keys override that and are removed on demande only
    }
  }
});

export const TypedQueryClientProvider = ({ children }: { children: ReactNode }) => <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;

function getQueryData<T>(queryKey: TypedQueryKey<T>) {
  return queryClient.getQueryData<T, ReadonlyArray<TypedQueryKey<T>>>([queryKey]);
}

function setQueryData<T>(queryKey: TypedQueryKey<T>, updater: NotAny<T>) {
  queryClient.setQueryData<NotAny<T>, ReadonlyArray<TypedQueryKey<T>>>([queryKey], updater);

  if (queryKey.origin === ORIGIN.Local) queryClient.setQueryDefaults([queryKey], { gcTime: Infinity }); // Local keys get removed on demand only
}

function invalidateQueries<T>(queryKey: TypedQueryKey<T>) {
  queryClient.invalidateQueries({ queryKey: [queryKey] });
}

function removeQueries<T>(queryKey: TypedQueryKey<T>) {
  queryClient.removeQueries({ queryKey: [queryKey] });
}

function resetQueries<T>(queryKey: TypedQueryKey<T>) {
  queryClient.resetQueries({ queryKey: [queryKey] });
}

function clear() {
  queryClient.clear();
}

export const typedQueryClient = { getQueryData, setQueryData, invalidateQueries, removeQueries, resetQueries, clear };

type TypeOfTypedQueryKey<K> = K extends TypedQueryKey<infer T> ? T : never;

export type UndefinedInitialDataTypedOptions<K> = Omit<
  UndefinedInitialDataOptions<TypeOfTypedQueryKey<K>, DefaultError, TypeOfTypedQueryKey<K>, ReadonlyArray<K>>,
  "queryKey" | "queryFn"
> & {
  queryKey: K;
  queryFn?: (queryKey: K) => TypeOfTypedQueryKey<K> | Promise<TypeOfTypedQueryKey<K>>;
};

type DefinedInitialDataTypedOptions<K> = Omit<
  DefinedInitialDataOptions<TypeOfTypedQueryKey<K>, DefaultError, TypeOfTypedQueryKey<K>, ReadonlyArray<K>>,
  "queryKey" | "queryFn"
> & {
  queryKey: K;
  queryFn?: (queryKey: K) => TypeOfTypedQueryKey<K> | Promise<TypeOfTypedQueryKey<K>>;
};

export function useTypedQuery<K>(options: DefinedInitialDataTypedOptions<K>): DefinedUseQueryResult<TypeOfTypedQueryKey<K>, DefaultError>;

export function useTypedQuery<K>(options: UndefinedInitialDataTypedOptions<K>): UseQueryResult<TypeOfTypedQueryKey<K>, DefaultError>;

export function useTypedQuery<K>(options: UndefinedInitialDataTypedOptions<K>) {
  const { queryKey, queryFn, gcTime, ...others } = options;

  return useQuery<TypeOfTypedQueryKey<K>, DefaultError, TypeOfTypedQueryKey<K>, ReadonlyArray<K>>({
    queryKey: [queryKey],
    queryFn: queryFn ? ({ queryKey }) => queryFn(queryKey[0]) : undefined,
    gcTime: gcTime !== undefined ? gcTime : (queryKey as TypedQueryKey<unknown>).origin === ORIGIN.Local ? Infinity : 0, // Local keys get removed on demand only
    ...others
  });
}
